import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, FlaskConical, FileCheck2, Stethoscope } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { useLang } from "@/contexts/LanguageContext";
import { useCategories } from "@/contexts/CategoriesContext";
import { resolveImageUrl } from "@/lib/api";

export default function Home() {
  const { settings: s } = useSettings();
  const { t } = useLang();
  const { categories } = useCategories();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 -z-10">
          <img
            alt=""
            src={resolveImageUrl(s.hero_image_url)}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/40" />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-12 pb-12 lg:pt-20 lg:pb-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 fade-up">
            <div className="overline mb-6">{s.tagline}</div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-foreground">
              {s.hero_headline_1 || "Clinical-grade peptides,"}
              <br />
              {s.hero_headline_2 || "dispensed under"}{" "}
              <span className="text-[hsl(var(--accent))]">{s.hero_headline_accent || "verified"}</span>{" "}
              {s.hero_headline_3 || "protocols."}
            </h1>
            <p className="mt-8 max-w-2xl text-base text-muted-foreground leading-relaxed">
              {s.hero_subtitle}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" data-testid="cta-doctor">
                <Button size="lg" className="rounded-md h-12 px-6">
                  {t("home.cta.doctor")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/catalog" className="text-sm font-medium underline underline-offset-4 ml-2" data-testid="cta-browse">
                {t("home.cta.browse")}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 lg:self-end">
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {[
                { k: s.stat_active_count || "23", v: s.stat_active_label || "active peptides" },
                { k: s.stat_lab_tested || "100%", v: s.stat_lab_tested_label || "lab-tested batches" },
                { k: s.rx_review_sla || "48h", v: "review SLA" },
                { k: s.shipping_zones || "EU/US", v: "shipping zones" },
              ].map((it) => (
                <div
                  key={it.v}
                  className="bg-white border border-border p-3 sm:p-5"
                >
                  <div className="font-display text-2xl sm:text-3xl font-bold tracking-tight break-words">{it.k}</div>
                  <div className="overline mt-1 text-[10px]">{it.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <div className="overline mb-3">{t("home.cat.overline")}</div>
        <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-10 lg:whitespace-nowrap">
          {t("home.cat.title")}
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {categories.map((c) => (
            <Link
              key={c.category_id}
              to={`/catalog?category=${c.slug}`}
              data-testid={`category-${c.slug}`}
              className="group relative overflow-hidden border border-border bg-white lift block"
            >
              <div className="aspect-[4/5] overflow-hidden bg-secondary">
                {c.image_url && (
                  <img
                    src={resolveImageUrl(c.image_url)}
                    alt={c.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                )}
              </div>
              <div className="p-6 flex items-end justify-between">
                <div>
                  <h3 className="font-display text-2xl font-semibold tracking-tight">{c.title}</h3>
                  {c.blurb && <p className="text-sm text-muted-foreground mt-1">{c.blurb}</p>}
                </div>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* QUALITY MARQUEE */}
      <section className="border-y border-border bg-[hsl(var(--secondary))] py-6 overflow-hidden">
        <div className="marquee-track flex gap-16 whitespace-nowrap text-sm font-mono uppercase tracking-widest text-muted-foreground w-max">
          {["a", "b"].map((k) => (
            <div key={`marquee-${k}`} className="flex gap-16">
              <span>· Lab-tested batches</span>
              <span>· HPLC purity ≥ 99%</span>
              <span>· Cold-chain shipping</span>
              <span>· Pharmacist-reviewed</span>
              <span>· Doctor-only access</span>
              <span>· Cooler bag included</span>
              <span>· License-verified</span>
            </div>
          ))}
        </div>
      </section>

      {/* QUALITY ASSURANCE */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div className="aspect-[4/3] overflow-hidden border border-border">
          <img
            src={resolveImageUrl(s.about_image_url)}
            alt="Lab"
            className="w-full h-full object-cover"
          />
        </div>
        <div>
          <div className="overline mb-3">{t("home.qa.overline")}</div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
            {t("home.qa.title")}
          </h2>
          <p className="mt-6 text-sm text-muted-foreground leading-relaxed max-w-xl">
            {t("home.qa.body")}
          </p>
          <ul className="mt-8 grid grid-cols-2 gap-3">
            {[
              { i: <ShieldCheck className="w-4 h-4" />, t: "Regulated dispensing" },
              { i: <FlaskConical className="w-4 h-4" />, t: "≥99% HPLC purity" },
              { i: <FileCheck2 className="w-4 h-4" />, t: "Per-batch CoA" },
              { i: <Stethoscope className="w-4 h-4" />, t: "Clinician-reviewed" },
            ].map((b) => (
              <li
                key={b.t}
                className="flex items-center gap-2 text-sm border border-border bg-white px-4 py-3"
              >
                <span className="text-[hsl(var(--accent))]">{b.i}</span>
                {b.t}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
